import {
  useParams,
  withRouter,
} from 'react-router';
import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import {
  BookingParams,
  Discount,
  MakeOrderResponse,
} from '../models/APIModels';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import {
  loadStripe,
  PaymentIntent,
  SetupIntent,
  StripeError,
  ConfirmCardSetupOptions,
  StripeCardElement,
  StripeCardNumberElement,
} from '@stripe/stripe-js';
import {
  PaymentProps,
  PaymentState,
  PaymentFormErrors,
  PaymentFormInitialValues,
  PaymentDefinedStateComponentProps,
  ApplePayButtonProps,
} from '../types/payment';
import DiscountComponent from '../components/DiscountComponent';
import {
  buildCheckoutLines,
  CheckoutLineInput,
} from '../utilities/checkoutAnalytics';
import {
  buildBoxOrderRequest,
  buildRemovalsOrderRequest,
  buildSuitcaseOrderRequest,
} from '../utilities/orderRequests';
import {
  PayTextField,
  PayElementField,
} from '../components/payment/PayField';
import { OrderGroup, OrderLine } from '../components/payment/OrderSummary';
import Button from '../components/Button';
import BookingLeg from '../components/booking/BookingLeg';
import Callout from '../components/Callout';
import { SummaryArrow } from '../components/BookingSummary';
import Checkbox from '../components/Checkbox';
import { SelectableCard } from '../components/SelectableCard';
import { Formik } from 'formik';
import {
  Address,
  validateAddress,
} from '../components/AddressLookup/address';
// v2 picker. The legacy default export debounces nothing, aborts nothing and
// filters Postcoder rows to type === 'ADD', which drops every result for a
// coarse query. See AddressLookupV2's header.
import AddressLookup from '../components/AddressLookup/AddressLookupV2';
import Title from '../components/Title';
import {
  boxToMoney,
  monthlyCostToMoney,
  payNow,
  toMoney,
  orderCostToMoney,
  suitcaseOrderToMoney,
} from '../utilities/MoneyConversion';
import { axiosKK } from '../services/networkRequest';
import NavigationStatus from '../components/NavigationStatus';
import useGtmEventTracking from '../components/useGtmEventTracking';
import errorMessage from '../services/errorMessage';
import { ThreeDSModal } from '../components/ThreeDSModal';
import { PaymentStatus } from '../models/APIModels';
import {
  COLLECTION_FEE,
  COLLECTION_SURCHARGE,
  SHIPPING_FEE,
  REDELIVER_FEE,
  REDELIVER_SURCHARGE,
  FURNITURE_DISASSEMBLY_FEE,
} from '../constants';
import UserContext from '../contexts/UserContext';
import { isOutOfArea, isDifferentArea } from '../utilities/Postcode';
import { adminCheck } from '../utilities/Role';
import { CreditCardIcon } from '@heroicons/react/24/solid';
import { mapCountryToCountryCode } from '../utilities/Country';
import { useFlow } from '../utilities/useFlow';
import PackingRequirementsModal from '../components/PackingRequirementsModal';
import { LinkIcon } from '../components/icons/LinkIcon';
import { KlarnaIcon } from '../components/icons/KlarnaIcon';
import { VisaIcon, MastercardIcon, AmexIcon, ApplePayIcon } from '../components/icons/CardBrand';
import { isValidCardholderName } from '../utilities/cardholderName';

// Removals Order Request Interface
export type { PaymentFormErrors, PaymentFormInitialValues } from '../types/payment';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY!!);

// The Card Elements mount inside a Stripe-hosted iframe, which cannot read
// our CSS custom properties — so the token values are repeated here as
// literals. Keep them in step with `_variables.scss`:
//   --kk-slate-800 #27272b   --kk-slate-500 #7a7585   --danger-text #991b1b
//
// 16px is load-bearing, not stylistic. iOS Safari zooms the viewport when a
// focused form control is under 16px, and these were 14px — so tapping the
// card number on an iPhone zoomed the checkout. DESIGN_LANGUAGE.md §9.
// The family was 'Montserrat', which appears nowhere in §2's stack.
const cardInputStyle = {
  style: {
    base: {
      fontSize: '16px',
      color: '#27272b',
      fontWeight: '500',
      fontFamily: "'Encode Sans Semi Expanded', 'Inter', sans-serif",
      '::placeholder': {
        color: '#7a7585',
      },
    },
    invalid: {
      color: '#991b1b',
    },
  },
};

const cardNumberOptions = {
  ...cardInputStyle,
  placeholder: '1234 5678 9012 3456',
};

const cardCvcOptions = {
  ...cardInputStyle,
  placeholder: 'CVC',
};

const cardExpiryOptions = {
  ...cardInputStyle,
  placeholder: 'MM / YY',
};

/** Reassurance mark for the page lead. Decorative — the sentence says it. */
function LockIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="4" y="8.6" width="12" height="8.4" rx="2" />
      <path d="M7.2 8.6V6.4a2.8 2.8 0 0 1 5.6 0v2.2" />
    </svg>
  );
}

/**
 * What the customer still has to do, in the order the page asks for it.
 *
 * The page already computed every one of these and then spent them on a
 * single `disabled={!isValid}`, so eight possible reasons arrived as one
 * dead button and no explanation. Same finding, and the same answer, as
 * CollectionDelivery in Phase 4 Track 4.8: keep the control live and say
 * what is missing.
 */
interface OutstandingItem {
  key: keyof PaymentFormErrors;
  label: string;
  /** Where to send focus when the customer submits without it. */
  fieldId: string;
}

const BILLING_ID = 'pay-billing-body';

function outstandingItems(
  errors: PaymentFormErrors,
  method: 'card' | 'klarna',
): OutstandingItem[] {
  const shared: OutstandingItem[] = [
    { key: 'address', label: 'a billing address', fieldId: BILLING_ID },
    {
      key: 'agreeToTerms',
      label: 'your agreement to the terms',
      fieldId: 'agree-to-terms',
    },
    {
      key: 'agreeToPackingRules',
      label: 'your agreement to the packing requirements',
      fieldId: 'agree-to-packing-rules',
    },
  ];

  const method_specific: OutstandingItem[] =
    method === 'card'
      ? [
          { key: 'name', label: 'the name on the card', fieldId: 'name' },
          {
            key: 'cardNumberValidation',
            label: 'your card number',
            fieldId: 'card-number',
          },
          {
            key: 'expiryDateValidation',
            label: 'the expiry date',
            fieldId: 'card-expiry',
          },
          {
            key: 'cvcValidation',
            label: 'the security code',
            fieldId: 'card-cvc',
          },
        ]
      : [
          {
            key: 'name',
            label: 'the email address for Klarna',
            fieldId: 'klarna-email',
          },
        ];

  return [...method_specific, ...shared].filter((item) => errors[item.key]);
}

/** "a, b and c" — the list reads as a sentence, not as a bullet list. */
function listSentence(items: string[]): string {
  if (items.length === 0) {
    return '';
  }
  if (items.length === 1) {
    return items[0];
  }
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Moves focus to the first thing still outstanding after a failed submit.
 * The card fields live inside Stripe-hosted iframes, so they are reached
 * through the Elements API rather than by id.
 */
function FocusFirstOutstanding({
  submitCount,
  item,
  elements,
}: {
  submitCount: number;
  item?: OutstandingItem;
  elements: ReturnType<typeof useElements>;
}): null {
  useEffect(() => {
    if (submitCount === 0 || !item) {
      return;
    }

    if (elements) {
      if (item.key === 'cardNumberValidation') {
        elements.getElement(CardNumberElement)?.focus();
        return;
      }
      if (item.key === 'expiryDateValidation') {
        elements.getElement(CardExpiryElement)?.focus();
        return;
      }
      if (item.key === 'cvcValidation') {
        elements.getElement(CardCvcElement)?.focus();
        return;
      }
    }

    const target = document.getElementById(item.fieldId);
    if (!target) {
      return;
    }
    target.scrollIntoView({ block: 'center' });
    target.focus({ preventScroll: true });
    // `item` is rebuilt every render; the submit attempt is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  return null;
}

function PaymentComponent({ history }: PaymentProps): JSX.Element {
  const { state: parsedState } = history.location;
  const { user } = useContext(UserContext);
  const flow = useFlow();
  const [userLoggedIn, setUserLoggedIn] = useState(user && !adminCheck(user));
  
  // CRITICAL: Check localStorage for existing order BEFORE initializing state
  const getInitialState = (): PaymentState | undefined => {
    // If coming fresh from booking flow, clear any previous order markers
    if ((parsedState as any)?.isFirstTimeTriggered) {
      sessionStorage.removeItem('order-created');
      sessionStorage.removeItem('created-order-id');
      sessionStorage.removeItem('order-collection-address');
      sessionStorage.removeItem('order-collection-date');
      return parsedState;
    }

    // Check sessionStorage first for order created in this tab session
    // Only if the order details match (same collection address + date)
    if (sessionStorage.getItem('order-created') === 'true') {
      const createdOrderId = sessionStorage.getItem('created-order-id');
      const storedCollectionAddress = sessionStorage.getItem('order-collection-address');
      const storedCollectionDate = sessionStorage.getItem('order-collection-date');

      // Only redirect if it's the SAME order (same address + date)
      if (createdOrderId && storedCollectionAddress && storedCollectionDate &&
          parsedState.collectionAddress &&
          parsedState.collectionDate &&
          storedCollectionAddress === parsedState.collectionAddress.line1 &&
          storedCollectionDate === parsedState.collectionDate.toISOString().split('T')[0]) {
        return { ...parsedState, orderId: parseInt(createdOrderId) } as PaymentState;
      }
    }

    const storedState = localStorage.getItem('payment-state');
    if (storedState) {
      try {
        const parsed = JSON.parse(storedState) as PaymentState;
        // If an order was already created, we'll handle redirect in useEffect
        if (parsed.orderId) {
          return parsed; // Return state with orderId for redirect check
        }
      } catch {
        // Invalid stored state, ignore
      }
    }
    return parsedState;
  };
  
  const [state, setState] = useState<PaymentState | undefined>(getInitialState);
  const isRemovalsFlow = history.location.pathname.includes('/removals') || 
                        (parsedState as any)?.removalService;

  // CRITICAL: Redirect if order already created - runs FIRST on mount
  useEffect(() => {
    if (state?.orderId) {
      const route = flow === 'Keepr' ? 'keepr' : flow === 'St Annes' ? 'stannes' : 'storage';
      history.replace(`/${route}/success`, state);
    }
  }, [state, history, flow]);

  // Save state to localStorage (but preserve orderId if it exists)
  useEffect(() => {
    if (state && !state.orderId) {
      // Only save if there's no orderId (don't overwrite completed order state)
      const storedState = localStorage.getItem('payment-state');
      if (storedState) {
        try {
          const parsed = JSON.parse(storedState);
          if (parsed.orderId) {
            // Don't overwrite - there's an order already
            return;
          }
        } catch {
          // Ignore
        }
      }
      localStorage.setItem('payment-state', JSON.stringify(state));
    } else if (!state) {
      const paymentState = localStorage.getItem('payment-state');
      if (paymentState) {
        try {
          const newState = JSON.parse(paymentState) as PaymentState;
          setState(newState);
        } catch {
          // Invalid stored state, ignore
        }
      }
    }
  }, [state]);

  useEffect(() => {
    setUserLoggedIn(user && !adminCheck(user));
  }, [user]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pay-page">
      <NavigationStatus
        step={isRemovalsFlow ? 5 : 3}
        locationState={state}
      />
      <div className="pay-page__intro">
        <Title>Payment</Title>
        {/*
          The lead was marketing copy — "<brand> are experts at storing and
          shipping boxes all across the UK" — at 2.7:1, on the one page where
          the customer has already decided and is handing over a card. What
          belongs here is the reassurance that buys the card number.
        */}
        <p className="pay-page__lead">
          <LockIcon />
          Your card details go straight to Stripe. They never reach our
          servers.
        </p>
      </div>

      {userLoggedIn ? (
        state && (
          <PaymentDefinedStateComponent
            paymentState={state}
            push={history.push}
            pathname={history.location.pathname}
          />
        )
      ) : (
        <div className="pay-login">
          {/* Was an <h1>, on a page whose title is already an <h1>. */}
          <h2 className="pay-login__title">Sign in to finish your booking</h2>
          <p className="pay-login__lead">
            Your order is saved. Signing in keeps it, and everything we collect
            from here, on your account.
          </p>

          <div className="pay-login__actions">
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={() =>
                history.push(`/register`, {
                  ...state,
                  fromConfirmation: true,
                } as PaymentState)
              }
            >
              Create an account
            </Button>

            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={() =>
                history.push(`/login`, {
                  ...state,
                  fromConfirmation: true,
                } as PaymentState)
              }
            >
              Sign in
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function ApplePayButton({
  paymentState,
  appliedDiscount,
  onPaymentSuccess,
  isEnabled,
}: ApplePayButtonProps) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<any>(null);

  useEffect(() => {
    if (!stripe) return;

    const {
      boxes,
      suitcases,
      suitcaseData,
      storageRequired,
      collectionAddress,
    } = paymentState;

    const needsEmptyBoxDelivery = boxes.some(
      (box) => box.name !== 'Own box' && box.count > 0,
    );

    let totalAmount = 0;

    if (suitcases && suitcaseData) {
      totalAmount = suitcases.length * suitcaseData.shippingCost;
    } else if (storageRequired) {
      const boxesTotal = boxes.reduce(
        (total, box) => total + box.count * box.purchaseCost,
        0,
      );
      totalAmount = boxesTotal + COLLECTION_FEE;

      if (needsEmptyBoxDelivery) {
        const deliveryFee = parseInt(process.env.REACT_APP_DELIVERY_FEE || '0');
        totalAmount += deliveryFee;
      }

      if (
        collectionAddress.postCode &&
        isOutOfArea(collectionAddress.postCode)
      ) {
        totalAmount += COLLECTION_SURCHARGE;
      }
    } else {
      const boxesTotal = boxes.reduce(
        (total, box) => total + box.count * box.purchaseCost,
        0,
      );

      // Calculate shipping cost
      const totalBoxes = boxes
        .filter((it) => it.chargeShipping)
        .map((it) => it.count);
      const totalBoxesCount = totalBoxes?.reduce((a, b) => a + b, 0);
      const ownBoxesCount = boxes
        .filter((it) => it.chargeShipping && it.name === 'Own box')
        .map((it) => it.count)
        .reduce((a, b) => a + b, 0);
      const otherBoxesCount = totalBoxesCount - ownBoxesCount;
      const deliveryCost =
        SHIPPING_FEE * otherBoxesCount + (SHIPPING_FEE + 500) * ownBoxesCount;

      totalAmount = boxesTotal + deliveryCost;

      if (needsEmptyBoxDelivery) {
        const deliveryFee = parseInt(process.env.REACT_APP_DELIVERY_FEE || '0');
        totalAmount += deliveryFee;
      }
    }

    // Store original total before discount for display items
    const originalTotalAmount = totalAmount;

    // Apply discount if available
    if (appliedDiscount?.upFront) {
      totalAmount =
        totalAmount * (1 - appliedDiscount.discountPercentage / 100);
    }

    // Create display items for Apple Pay sheet
    const displayItems = [];

    // Add box items
    boxes.forEach((box) => {
      if (box.count > 0) {
        displayItems.push({
          label: `${box.name} × ${box.count}`,
          amount: box.count * box.purchaseCost,
        });
      }
    });

    // Add fees
    if (storageRequired) {
      displayItems.push({
        label: 'Collection fee',
        amount: COLLECTION_FEE,
      });

      if (
        collectionAddress.postCode &&
        isOutOfArea(collectionAddress.postCode)
      ) {
        displayItems.push({
          label: 'Collection surcharge',
          amount: COLLECTION_SURCHARGE,
        });
      }
    }

    if (needsEmptyBoxDelivery) {
      const deliveryFee = parseInt(process.env.REACT_APP_DELIVERY_FEE || '0');
      displayItems.push({
        label: 'Delivery of empty boxes',
        amount: deliveryFee,
      });
    }

// Add discount line
if (appliedDiscount?.upFront) {
  const discountAmount = Math.round((originalTotalAmount * appliedDiscount.discountPercentage) / 100);
  
  if (isFinite(discountAmount) && discountAmount > 0) {
    displayItems.push({
      label: `Discount (${appliedDiscount.discountPercentage}%)`,
      amount: -discountAmount,  // ✅ Now an integer in pence
    });
  }
}

    // Validate all displayItems amounts are finite numbers
    const validDisplayItems = displayItems.filter((item) => 
      isFinite(item.amount) && !isNaN(item.amount)
    );

    // Ensure totalAmount is valid
    const finalAmount = isFinite(totalAmount) && !isNaN(totalAmount) ? Math.round(totalAmount) : 0;

    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: 'gbp',
      total: {
        label: 'Kit Keeper',
        amount: finalAmount, // Stripe expects amounts in pence
      },
      displayItems: validDisplayItems,
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check if Apple Pay is available
    pr.canMakePayment().then((result: any) => {
      if (result) {
        setPaymentRequest(pr);
      }
    });

    pr.on('paymentmethod', async (event: any) => {
      // Check if enabled before processing
      if (!isEnabled) {
        event.complete('fail');
        return;
      }
      // Await backend response before closing Apple Pay sheet
      try {
        await onPaymentSuccess(event.paymentMethod);
        event.complete('success');
      } catch {
        event.complete('fail');
      }
    });
  }, [stripe, paymentState, appliedDiscount, onPaymentSuccess, isEnabled]);

  if (!paymentRequest) {
    return null;
  }

  return (
    <div className={`pay-express${!isEnabled ? ' pay-express--blocked' : ''}`}>
      <p className="pay-express__divider">or pay with</p>

      <div className="pay-express__button">
        <PaymentRequestButtonElement options={{ paymentRequest }} />
      </div>

      {/*
        The wallet is gated on the two agreements, which sit with the pay
        button further down. Say so — the button previously sat under a
        transparent overlay and simply did not respond.
      */}
      {!isEnabled && (
        <p className="pay-express__blocked">
          Agree to the terms and the packing requirements below to pay this
          way.
        </p>
      )}
    </div>
  );
}


function PaymentDefinedStateComponent({
  paymentState: state,
  push,
  pathname,
}: PaymentDefinedStateComponentProps) {
  const {
    boxes,
    collectionAddress,
    deliveryAddress,
    suitcases,
    storageRequired,
    suitcaseData,
  } = state;

  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [billingAddress, setBillingAddress] = useState<Address>({});
  const [orderId, setOrderId] = useState<number>();
  const [appliedDiscount, setAppliedDiscount] = useState<Discount>();
  const { route: routeParam } = useParams<BookingParams>();
  const isRemovalsFlow = pathname.includes('/removals') || state.removalService;
  const route = isRemovalsFlow ? 'removals' : routeParam;
  const [showPayNowDetails, setShowPayNowDetails] = useState(false);
  const [showPayMonthlyDetails, setShowPayMonthlyDetails] = useState(false);
  const [showFinalPaymentDetails, setShowFinalPaymentDetails] = useState(false);
  const [showPackingModal, setShowPackingModal] = useState(false);
  
  // CRITICAL: Track if order was created to prevent duplicate submissions
  // Check sessionStorage first (survives navigation within tab)
  const orderCreatedRef = useRef<boolean>(
    sessionStorage.getItem('order-created') === 'true'
  );
  
  // Clear localStorage and mark order as created
  const markOrderCreated = useCallback((newOrderId: number) => {
    orderCreatedRef.current = true;
    // Mark in sessionStorage (survives back/forward navigation)
    sessionStorage.setItem('order-created', 'true');
    sessionStorage.setItem('created-order-id', String(newOrderId));
    // Store order details to prevent false positives on different orders
    sessionStorage.setItem('order-collection-address', state.collectionAddress?.line1 || '');
    sessionStorage.setItem('order-collection-date', state.collectionDate?.toISOString().split('T')[0] || '');
    // Store orderId in payment state
    const currentState = localStorage.getItem('payment-state');
    if (currentState) {
      try {
        const parsed = JSON.parse(currentState);
        parsed.orderId = newOrderId;
        localStorage.setItem('payment-state', JSON.stringify(parsed));
      } catch {
        // Ignore parse errors
      }
    }
  }, [state.collectionAddress, state.collectionDate]);
  
  // CRITICAL: Check sessionStorage on mount for order already created in this session
  useEffect(() => {
    if (sessionStorage.getItem('order-created') === 'true') {
      orderCreatedRef.current = true;
      const createdOrderId = sessionStorage.getItem('created-order-id');
      if (createdOrderId) {
        push(`/${route}/success`, { ...state, orderId: parseInt(createdOrderId) });
      }
    }
  }, [push, route, state]);

  const totalBoxes = boxes
    .filter((it) => it.chargeShipping)
    .map((it) => it.count);
  const totalBoxesCount = totalBoxes?.reduce((a, b) => a + b, 0);

  const ownBoxesCount = boxes
    .filter((it) => it.chargeShipping && it.name === 'Own box')
    .map((it) => it.count)
    .reduce((a, b) => a + b, 0);
  const otherBoxesCount = totalBoxesCount - ownBoxesCount;

  const shippingRate = SHIPPING_FEE;
  const deliveryCost =
    shippingRate * otherBoxesCount + (shippingRate + 500) * ownBoxesCount;
  const needsEmptyBoxDelivery = boxes.some(
    (box) => box.name !== 'Own box' && box.count > 0,
  );

  const [billingEventSentToGTM, setBillingEventSentToGTM] = useState(false);
  const [paymentEventSentToGTM, setPaymentEventSentToGTM] = useState(false);

  const [selectedBillingOption, setSelectedBillingOption] = useState<
    string | null
  >('collection');
  const [showBillingAddressLookup, setShowBillingAddressLookup] =
    useState(false);

  // 3D Security
  const frontendUrl = process.env.REACT_APP_FRONTEND_URL;
  const [threeDSRedirectUrl, setThreeDSRedirectUrl] = useState<string>('');
  const [intentClientSecret, setIntentClientSecret] = useState<
    string | undefined
  >(undefined);
  const [paymentError, setPaymentError] = useState<string | undefined>(
    undefined,
  );
  const [paymentMethodType, setPaymentMethodType] = useState<'card' | 'klarna'>('card');
  const [klarnaEmail, setKlarnaEmail] = useState('');

  let orderType: string;
  if (suitcases) {
    orderType = 'Suitcase';
  } else {
    if (storageRequired) {
      orderType = 'Box Storage';
    } else {
      orderType = 'Box Shipping';
    }
  }

  const numberOfBoxesWithReturnFee = boxes
    .filter((box) => box.returnFee && box.count > 0)
    .reduce((total, box) => total + box.count, 0);

  const returnFeeTotal = numberOfBoxesWithReturnFee * REDELIVER_FEE;

  const differentAreaSurcharge =
    storageRequired &&
    collectionAddress.postCode &&
    deliveryAddress?.postCode &&
    isDifferentArea(collectionAddress.postCode, deliveryAddress.postCode)
      ? numberOfBoxesWithReturnFee * REDELIVER_SURCHARGE
      : 0;

  const finalPaymentTotal = returnFeeTotal + differentAreaSurcharge;

  const checkoutLines: CheckoutLineInput[] =
    suitcases && suitcaseData
      ? suitcases.map((_, i) => ({
          id: suitcaseData.id,
          name: suitcaseData.name,
          pricePence: suitcaseData.purchaseCost,
          category: orderType,
          index: i,
        }))
      : boxes.map((box, i) => ({
          id: box.id,
          name: box.name,
          pricePence: box.purchaseCost,
          category: orderType,
          index: i,
        }));

  // Storage is reported as one rolled-up line rather than per box, and
  // only on a storage order.
  const storageCost =
    !suitcases && orderType === 'Storage'
      ? boxes.reduce((total, box) => total + box.storageCost, 0)
      : 0;

  const purchased = buildCheckoutLines(checkoutLines, appliedDiscount);
  // Built without the discount on purpose: the roll-up has never carried
  // the coupon fields, and changing what reaches the dataLayer would split
  // the existing reports rather than improve them.
  const storage =
    storageCost > 0
      ? buildCheckoutLines([
          {
            name: 'Box Storage',
            pricePence: storageCost,
            category: 'Storage',
            index: 0,
          },
        ])
      : { products: [], items: [] };

  const eecCheckoutProducts = [...purchased.products, ...storage.products];
  const beginCheckoutItems = [...purchased.items, ...storage.items];

  /**
   * What the customer pays today for a catalogue booking — boxes, shipping
   * or suitcases. This three-way branch was written out three times: the
   * summary figure and both analytics payloads.
   *
   * Declared here rather than beside the other figures because
   * `eecPaymentObject` below reads it during render, not in a callback.
   */
  const catalogueTotal = (): string =>
    suitcases
      ? suitcaseOrderToMoney(
          suitcases.length,
          suitcaseData?.shippingCost,
          appliedDiscount,
        )
      : storageRequired
      ? payNow(boxes, appliedDiscount, needsEmptyBoxDelivery, collectionAddress)
      : orderCostToMoney(boxes, needsEmptyBoxDelivery, appliedDiscount);

  /**
   * The removals "pay now" base — tier, packing and disassembly, before any
   * discount. The toggle figure and the discount line beneath it were each
   * computing this inline, which is two places to keep one number right.
   */
  const removalsPayNowBase = () =>
    (state.selectedTier?.price || 0) +
    (state.selectedPacking?.price || 0) +
    (state.questions?.hasDisassembly ? FURNITURE_DISASSEMBLY_FEE : 0);

  /**
   * What the customer pays today, whatever they are booking — the figure
   * shown as "Pay Now", and the value reported to Meta Pixel and GA4.
   *
   * It lives up here, above the analytics payloads, because
   * `eecPaymentObject` reads it during render rather than in a callback.
   *
   * The removals case matters: without it this falls through to
   * `orderCostToMoney(boxes, …)` and reports a box-order total for a
   * booking that is not a box order. Every removals checkout used to report
   * whatever the (usually empty) box list came to — near zero in practice —
   * while the customer was charged the tier price plus packing and
   * disassembly. Fixed 2026-09-14; regression-tested in `Payment.test.tsx`.
   */
  const payNowTotal = (): string =>
    state.removalService && state.selectedTier
      ? toMoney(
          removalsPayNowBase() *
            (appliedDiscount?.upFront
              ? 1 - appliedDiscount.discountPercentage / 100
              : 1),
        )
      : catalogueTotal();

  const gtmEventTracking = useGtmEventTracking();

  useEffect(() => {
    if (state?.isFirstTimeTriggered) {
      gtmEventTracking({
        event: 'begin_checkout',
        ecommerce: {
          items: beginCheckoutItems,
        },
      });

      gtmEventTracking({
        event: 'eec_checkout',
        ecommerce: {
          checkout: {
            actionField: {
              step: 1,
            },
            products: eecCheckoutProducts,
          },
        },
      });

      gtmEventTracking({
        event: 'booking_step_payment',
      });

      // Meta Pixel: Track InitiateCheckout
      const checkoutValue = Number.parseFloat(
        payNowTotal().substring(1),
      );
      window.fbq('track', 'InitiateCheckout', {
        value: checkoutValue,
        currency: 'GBP',
        num_items: beginCheckoutItems?.length || 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtmEventTracking, state?.isFirstTimeTriggered]);

  useEffect(() => {
    setBillingAddress(collectionAddress);
  }, [collectionAddress]);

  useEffect(() => {
    if (appliedDiscount) {
      if (appliedDiscount.upFront) {
        setShowPayNowDetails(true);
      }
      if (appliedDiscount.recurring && storageRequired) {
        setShowPayMonthlyDetails(true);
      }
    }
  }, [appliedDiscount, storageRequired]);

  const eecBillingObject = {
    event: 'eec_checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step: 2,
        },
      },
    },
  };

  const billingInfoObject = {
    event: 'add_billing_info',
    ecommerce: {
      items: beginCheckoutItems,
    },
  };

  const eecPaymentObject = {
    event: 'eec_checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step: 3,
        },
      },
    },
  };

  const paymentInfoObject = {
    event: 'add_payment_info',
    ecommerce: {
      currency: 'GBP',
      value: Number.parseFloat(
        payNowTotal().substring(1),
      ),
      coupon: appliedDiscount ? appliedDiscount.discountCode : null,
      payment_type: 'Credit Card',
      items: beginCheckoutItems,
    },
  };

  const eventTrackingCallback = useCallback(
    (data: any) => {
      gtmEventTracking(data);
    },
    [gtmEventTracking],
  );

  const initialValues: PaymentFormInitialValues = {
    name: '',
    cardNumberValidation: false,
    cvcValidation: false,
    expiryDateValidation: false,
    address: '',
    agreeToTerms: false,
    agreeToPackingRules: false,
  };

  const handleIntent = useCallback(
    (
      intent: PaymentIntent | SetupIntent,
      orderId: number | undefined,
      finalState: PaymentState,
    ) => {
      const action = intent.next_action;
      if (
        intent.status === 'requires_action' &&
        action?.type === 'redirect_to_url'
      ) {
        //  Modal that contains an iframe
        const url = action?.redirect_to_url?.url;

        if (url) {
          setThreeDSRedirectUrl(url);

          push(`/${route}/payment/3ds-secure`);
        }
      } else if (intent.status === 'succeeded') {
        if (orderId) markOrderCreated(orderId);
        push(`/${route}/success`, { ...finalState, orderId });
      }
    },
    [push, route, setThreeDSRedirectUrl, markOrderCreated],
  );

  const handlePaymentStatus = useCallback(
    (
      data: MakeOrderResponse,
      finalState: PaymentState,
      cardNumber: StripeCardElement | StripeCardNumberElement,
      paymentMethodId: string,
    ) => {
      switch (data.paymentStatus) {
        case PaymentStatus.noneNeeded:
        case PaymentStatus.succeeded:
        case PaymentStatus.trialing:
          markOrderCreated(data.order.id);
          push(`/${route}/success`, { ...finalState, orderId: data.order.id });
          break;
        
        case PaymentStatus.duplicatePrevented:
          // Backend detected duplicate - redirect to existing order
          markOrderCreated(data.order.id);
          push(`/${route}/success`, { ...finalState, orderId: data.order.id });
          break;
  
        case PaymentStatus.requiresAction:
          // For wallet payments (Apple Pay), the payment method is already
          // authenticated — use the paymentMethodId string directly.
          // For card payments, build confirm data with the card element.
          const isWalletPayment = !cardNumber;
          const confirmData: any = isWalletPayment
            ? {
                payment_method: paymentMethodId,
                return_url: `${frontendUrl}/${route}/back-to-payment`,
              }
            : {
                payment_method: {
                  card: cardNumber,
                },
                return_url: `${frontendUrl}/${route}/back-to-payment`,
              };
          const confirmOptions: ConfirmCardSetupOptions = {
            handleActions: false,
          };
          if (stripe) {
            if (data.paymentIntentSecret) {
              stripe
                .confirmCardPayment(
                  data.paymentIntentSecret,
                  confirmData,
                  confirmOptions,
                )
                .then(({ paymentIntent }) => {
                  if (paymentIntent) {
                    handleIntent(paymentIntent, data.order.id, finalState);
                  }
                })
                .catch((error) => {
                  setPaymentError(error.message || 'Payment confirmation failed');
                });
            } else if (data.setupIntentSecret) {
              stripe
                .confirmCardSetup(
                  data.setupIntentSecret,
                  confirmData,
                  confirmOptions,
                )
                .then(({ setupIntent }) => {
                  if (setupIntent) {
                    handleIntent(setupIntent, data.order.id, finalState);
                  }
                })
                .catch((error) => {
                  setPaymentError(error.message || 'Setup confirmation failed');
                });
            }
          }
          break;
  
        case PaymentStatus.requiresPaymentMethod:
          setPaymentError('Please try another payment method');
          break;
      }
    },
    [
      stripe,
      push,
      route,
      frontendUrl,
      handleIntent,
      setPaymentError,
      markOrderCreated,
    ],
  );

  const calculateDiscountAmount = () => {
    if (!appliedDiscount) return '';

    let baseAmount = 0;

    if (suitcases && suitcaseData) {
      baseAmount = suitcases.length * suitcaseData.shippingCost;
    } else if (storageRequired) {
      const boxesTotal = boxes.reduce(
        (total, box) => total + box.count * box.purchaseCost,
        0,
      );
      baseAmount = boxesTotal + COLLECTION_FEE;
      if (
        collectionAddress.postCode &&
        isOutOfArea(collectionAddress.postCode)
      ) {
        baseAmount += COLLECTION_SURCHARGE;
      }
    } else {
      const boxesTotal = boxes.reduce(
        (total, box) => total + box.count * box.purchaseCost,
        0,
      );
      baseAmount = boxesTotal + deliveryCost;
    }

    const discountAmount =
      (baseAmount * appliedDiscount.discountPercentage) / 100;
    return toMoney(discountAmount);
  };

  const calculateMonthlyDiscountAmount = () => {
    if (!appliedDiscount || !appliedDiscount.recurring) return '';

    const monthlyTotal = boxes.reduce(
      (total, box) => total + box.storageCost * box.count,
      0,
    );
    const discountAmount =
      (monthlyTotal * appliedDiscount.discountPercentage) / 100;
    return toMoney(discountAmount);
  };

  /**
   * Abandon the submit: say why, and put the button back so the customer
   * can correct and retry.
   *
   * Pairing the two was left to each branch, and there are eleven of them.
   * A branch that reports the error but forgets `setSubmitting(false)`
   * leaves "Confirm payment" spinning with no way out — so the pair is one
   * call now rather than a convention to remember.
   */
  const fail = (reason: string | Error): void => {
    errorMessage(setError)(reason);
    setSubmitting(false);
  };

  /**
   * The billing address in the shape Stripe expects. Built identically for
   * `createPaymentMethod` on the card path and `confirmKlarnaPayment` on
   * the Klarna path — and a mismatch between the two would mean the same
   * customer's address reaching Stripe differently depending on how they
   * chose to pay.
   */
  const stripeBillingAddress = () => ({
    line1: billingAddress.line1,
    line2: billingAddress.line2,
    city: billingAddress.city,
    state: billingAddress.county,
    postal_code: billingAddress.postCode,
    country: mapCountryToCountryCode(billingAddress.country),
  });

  /**
   * The state handed on to the success page, and stashed in localStorage
   * for the Klarna return trip.
   *
   * This 22-field object was written out four times — byte for byte
   * identically — across the card path, the Klarna path, the removals path
   * and the Apple Pay handler. Four copies of the payload that decides what
   * a customer sees after paying is four chances for one of them to drift.
   */
  const buildFinalState = () => ({
    boxes: boxes,
    collectionAddress: collectionAddress,
    collectionDate: state.collectionDate,
    deliveryAddress: deliveryAddress,
    dontKnowAddress: deliveryAddress == null,
    deliveryDate: state.deliveryDate,
    dontKnowDate: state.deliveryDate == null,
    storageRequired: storageRequired,
    suitcases: suitcases,
    suitcaseData: suitcaseData,
    eecProducts: eecCheckoutProducts,
    items: beginCheckoutItems,
    orderId: orderId,
    discount: appliedDiscount,
    // Removals specific fields
    removalService: state.removalService,
    selectedTier: state.selectedTier,
    questions: state.questions,
    moveDate: state.moveDate,
    selectedPacking: state.selectedPacking,
    selectedStorage: state.selectedStorage,
    wantsPacking: state.wantsPacking,
    wantsStorage: state.wantsStorage,
  });

  // Which summary groups appear, named once because each condition was
  // written twice — on the group, and again on the footnote that explains
  // its asterisk. They had drifted: a removals booking with storage showed
  // "Pay Monthly*" while the note the asterisk points at was gated on
  // `storageRequired`, so the marker pointed at nothing.
  const showMonthlyGroup =
    storageRequired || Boolean(state.removalService && state.selectedStorage);
  const showRedeliveryGroup = storageRequired;

  const removalsMonthlyTotal = () =>
    (state.selectedStorage?.price || 0) *
    (appliedDiscount?.recurring
      ? 1 - appliedDiscount.discountPercentage / 100
      : 1);

  const handleRetrieveIntent = useCallback(
    (
      intent: PaymentIntent | SetupIntent | undefined,
      error: StripeError | undefined,
    ) => {
      if (error) {
        setPaymentError(error.message);
      } else if (intent?.status === PaymentStatus.succeeded) {
        if (orderId) markOrderCreated(orderId);
        push(`/${route}/success`, { ...state, orderId });
      } else if (intent?.status === PaymentStatus.requiresPaymentMethod) {
        setPaymentError('Please try another payment method');
      }
    },
    [push, route, state, orderId, setPaymentError, markOrderCreated],
  );

  useEffect(() => {
    let listener = (event: MessageEvent) => {
      if (
        event.origin === frontendUrl &&
        event.data === '3DS-authentication-complete'
      ) {
        intentClientSecret &&
          stripe!
            .retrievePaymentIntent(intentClientSecret)
            .then(({ paymentIntent, error }) => {
              handleRetrieveIntent(paymentIntent, error);
            });
      }
    };
    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  });

  const deliveryFeeString = process.env.REACT_APP_DELIVERY_FEE;
  if (!deliveryFeeString) {
    throw new Error('No delivery fee set');
  }
  const deliveryFee = parseInt(deliveryFeeString);

  const flow = useFlow();

  const handleApplePaySuccess = async (paymentMethod: any) => {
    // CRITICAL: Prevent duplicate submissions
    if (orderCreatedRef.current) {
      // Order already created in this session - redirect to success
      const createdOrderId = sessionStorage.getItem('created-order-id');
      if (createdOrderId) {
        push(`/${route}/success`, { ...state, orderId: parseInt(createdOrderId) });
      }
      return;
    }
    
    if (submitting) return;
    
    setSubmitting(true);
    try {
      const finalState = buildFinalState();

      let data: any;

      // Check if this is a removals order
      if (state.removalService && state.selectedTier && state.moveDate) {
        const removalRequest = buildRemovalsOrderRequest(
          state,
          flow,
          { paymentMethodId: paymentMethod.id },
          appliedDiscount,
        );

        const response = await axiosKK.post<any>('/removals/order/create', removalRequest);
        data = response.data;
      } else {
        const identity = { paymentMethodId: paymentMethod.id };
        const suitcaseRequest = buildSuitcaseOrderRequest(
          state,
          flow,
          identity,
          appliedDiscount,
        );
        const request = buildBoxOrderRequest(
          state,
          flow,
          identity,
          appliedDiscount,
        );

        const response = await axiosKK.post<MakeOrderResponse>(
          suitcases ? '/order/create/suitcase' : '/order/create',
          suitcases ? suitcaseRequest : request,
        );
        data = response.data;
      }

      const order = data.order;
      setOrderId(order.id);

      // Handle payment status (same as existing logic)
      handlePaymentStatus(data, finalState, null as any, paymentMethod.id);
    } catch (error) {
      fail(error as Error);
    }
  };

  return (
    <>
      <div className="card-payment-section">
        <Formik
          initialValues={initialValues}
          validateOnMount={true}
          validate={({
            name,
            cardNumberValidation,
            cvcValidation,
            expiryDateValidation,
            agreeToTerms,
            agreeToPackingRules,
          }) => {
            const errors: PaymentFormErrors = {};

            setBillingAddress(collectionAddress);
            if (!billingEventSentToGTM) {
              setBillingEventSentToGTM(true);
              eventTrackingCallback(eecBillingObject);
              eventTrackingCallback(billingInfoObject);
            }

            if (!validateAddress(billingAddress)) {
              errors.address = 'Invalid address';
            }

            if (paymentMethodType === 'card') {
              if (!isValidCardholderName(name)) {
                errors.name = 'too short of a name';
              }

              if (!cardNumberValidation) {
                errors.cardNumberValidation = 'Card number incomplete';
              }

              // These two used to write to `cardNumberValidation` as well,
              // so whichever ran last owned the message and a missing CVC
              // reported itself as a card-number problem. Each field now
              // carries its own key, which is what lets the page mark the
              // right box and send focus to it.
              if (!cvcValidation) {
                errors.cvcValidation = 'CVC incomplete';
              }

              if (!expiryDateValidation) {
                errors.expiryDateValidation = 'Expiry date incomplete';
              }
            } else {
              if (!klarnaEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(klarnaEmail)) {
                errors.name = 'Valid email is required for Klarna';
              }
            }

            if (!agreeToTerms) {
              errors.agreeToTerms =
                'You must agree to the terms and conditions';
            }

            if (!agreeToPackingRules) {
              errors.agreeToPackingRules =
                'You must agree to the packing requirements';
            }

            if (Object.keys(errors).length === 0 && !paymentEventSentToGTM) {
              setPaymentEventSentToGTM(true);
              eventTrackingCallback(eecPaymentObject);
              eventTrackingCallback(paymentInfoObject);
            }

            return errors;
          }}
          onSubmit={async ({ name }) => {
            // CRITICAL: Prevent duplicate submissions
            if (orderCreatedRef.current) {
              // Order already created in this session - redirect to success
              const createdOrderId = sessionStorage.getItem('created-order-id');
              if (createdOrderId) {
                push(`/${route}/success`, { ...state, orderId: parseInt(createdOrderId) });
              } else {
                setError('This order has already been submitted. Redirecting...');
                setTimeout(() => {
                  push(`/${route}/success`, state);
                }, 2000);
              }
              return;
            }
            
            if (submitting) {
              setError('Payment is already being processed. Please wait...');
              return;
            }
            
            try {
              setSubmitting(true);

              if (!stripe) {
                return fail('Payment gateway not prepared');
              }

              // Klarna payment flow
              if (paymentMethodType === 'klarna') {
                const finalState = buildFinalState();

                // Save state to localStorage for retrieval after Klarna redirect
                localStorage.setItem('klarna-payment-state', JSON.stringify(finalState));
                localStorage.setItem('klarna-return-route', route);

                // Klarna is confirmed after a redirect, so the order is
                // created with the method type rather than a payment-method
                // id. Same three request shapes as the card path.
                const identity = { paymentMethodType: 'klarna' } as const;

                const [endpoint, orderRequest] =
                  state.removalService && state.selectedTier && state.moveDate
                    ? [
                        '/removals/order/create',
                        buildRemovalsOrderRequest(
                          state,
                          flow,
                          identity,
                          appliedDiscount,
                        ),
                      ]
                    : suitcases
                    ? [
                        '/order/create/suitcase',
                        buildSuitcaseOrderRequest(
                          state,
                          flow,
                          identity,
                          appliedDiscount,
                        ),
                      ]
                    : [
                        '/order/create',
                        buildBoxOrderRequest(
                          state,
                          flow,
                          identity,
                          appliedDiscount,
                        ),
                      ];

                const { data } = await axiosKK.post<any>(endpoint, orderRequest);
                const clientSecret = data.paymentIntentSecret;

                if (!clientSecret) {
                  return fail('Failed to create Klarna payment');
                }

                // Save orderId for the return page
                localStorage.setItem('klarna-order-id', String(data.order?.id || ''));

                const returnUrl = `${frontendUrl}/${route}/klarna-return`;

                const { error: klarnaError } = await (stripe as any).confirmKlarnaPayment(clientSecret, {
                  payment_method: {
                    billing_details: {
                      email: klarnaEmail,
                      name: name,
                      address: stripeBillingAddress(),
                    },
                  },
                  return_url: returnUrl,
                });

                if (klarnaError) {
                  return fail(klarnaError.message || 'Klarna payment failed');
                }
                // If no error, user has been redirected to Klarna
                return;
              }

              // Card payment flow
              if (!elements) {
                return fail('Payment gateway not prepared');
              }

              const cardNumber = elements.getElement(CardNumberElement);
              const cvcNumber = elements.getElement(CardCvcElement);
              const expiryDate = elements.getElement(CardExpiryElement);

              if (!(cardNumber && cvcNumber && expiryDate)) {
                return fail('Card elements not ready');
              }

              const { paymentMethod, error } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardNumber,
                billing_details: {
                  name,
                  address: stripeBillingAddress(),
                },
              });

              if (error) {
                return fail(error.message || error.toString());
              }

              if (!paymentMethod) {
                return fail('Unable to retrieve payment method');
              }

            // Check if this is a removals order
            if (state.removalService && state.selectedTier && state.moveDate) {
              const finalState = buildFinalState();
              const removalRequest = buildRemovalsOrderRequest(
                state,
                flow,
                { paymentMethodId: paymentMethod.id },
                appliedDiscount,
              );

              // Create removals order
              axiosKK
                .post<any>('/removals/order/create', removalRequest)
                .then(async ({ data }) => {
                  const order = data.order;
                  setOrderId(order.id);

                  setIntentClientSecret(data.paymentIntentSecret);

                  // Handle payment status
                  handlePaymentStatus(
                    data,
                    finalState,
                    cardNumber,
                    paymentMethod.id,
                  );

                  return order.id;
                })
                .catch((error) => {
                  errorMessage(setError)(error as Error);
                })
                .finally(() => setSubmitting(false));
            } else {
              const identity = { paymentMethodId: paymentMethod.id };
              const suitcaseRequest = buildSuitcaseOrderRequest(
                state,
                flow,
                identity,
                appliedDiscount,
              );
              const request = buildBoxOrderRequest(
                state,
                flow,
                identity,
                appliedDiscount,
              );

              const finalState = buildFinalState();

              axiosKK
                .post<MakeOrderResponse>(
                  suitcases ? '/order/create/suitcase' : '/order/create',
                  suitcases ? suitcaseRequest : request,
                )
                .then(async ({ data }) => {
                  const order = data.order;
                  setOrderId(order.id);

                  setIntentClientSecret(data.paymentIntentSecret);

                  handlePaymentStatus(
                    data,
                    finalState,
                    cardNumber,
                    paymentMethod.id,
                  );

                  return order.id;
                })
                .catch((error) => {
                  errorMessage(setError)(error as Error);
                })
                .finally(() => setSubmitting(false));
            }
            } catch (error) {
              fail(error as Error);
            }
          }}
        >
          {({
            values,
            handleChange,
            handleSubmit,
            setFieldValue,
            errors,
            submitCount,
          }) => {
            // Formik has already run `validate` by the time a failed submit
            // renders, so the outstanding list is derived rather than kept
            // in a second piece of state.
            const attempted = submitCount > 0;
            const outstanding = outstandingItems(errors, paymentMethodType);
            const fieldError = (key: keyof PaymentFormErrors) =>
              attempted ? errors[key] : undefined;

            return (
            <form className="pay-page__form" onSubmit={handleSubmit} noValidate>
              <FocusFirstOutstanding
                submitCount={submitCount}
                item={outstanding[0]}
                elements={elements}
              />

              <div className="pay-page__grid">
                <div className="pay-page__main">
                  <BookingLeg
                    step={1}
                    id="pay-method"
                    title={storageRequired ? 'Card details' : 'How to pay'}
                  >
                    {/*
                      The marks are decorative and were six unlabelled SVGs.
                      Named once, as a list, rather than six times over.
                    */}
                    <div className="pay-accepted">
                      <span className="pay-accepted__label">We accept</span>
                      <div
                        className="pay-accepted__icons"
                        role="img"
                        aria-label="Visa, Mastercard, American Express, Apple Pay, Link and Klarna"
                      >
                        <VisaIcon />
                        <MastercardIcon />
                        <AmexIcon />
                        <ApplePayIcon />
                        <LinkIcon />
                        <KlarnaIcon />
                      </div>
                    </div>

                    {/*
                      Was a pair of <button>s styled as tabs, with no role, no
                      aria-selected and no grouping — so neither the choice
                      nor which option was taken reached assistive tech. It is
                      a radio group, and the primitive already draws one.
                    */}
                    {!storageRequired && (
                      <div
                        className="pay-method"
                        role="radiogroup"
                        aria-label="Payment method"
                      >
                        <SelectableCard
                          variant="single"
                          name="payment-method"
                          value="card"
                          checked={paymentMethodType === 'card'}
                          onChange={() => setPaymentMethodType('card')}
                          leadingIcon={<CreditCardIcon width={20} height={20} />}
                          label="Card"
                          description="Pay in full now, by debit or credit card."
                        />
                        <SelectableCard
                          variant="single"
                          name="payment-method"
                          value="klarna"
                          checked={paymentMethodType === 'klarna'}
                          onChange={() => setPaymentMethodType('klarna')}
                          leadingIcon={<KlarnaIcon width={20} height={20} />}
                          label="Klarna"
                          description="Spread the cost. We will send you to Klarna to finish."
                        />
                      </div>
                    )}

                    {paymentMethodType === 'card' ? (
                      <div className="pay-fields">
                        <PayTextField
                          id="name"
                          name="name"
                          label="Name on card"
                          value={values.name}
                          onChange={handleChange}
                          autoComplete="cc-name"
                          placeholder="As printed on the card"
                          error={
                            fieldError('name') &&
                            'Enter the name exactly as it appears on the card.'
                          }
                        />

                        <PayElementField
                          id="card-number"
                          label="Card number"
                          error={
                            fieldError('cardNumberValidation') &&
                            'Enter the long number across the front of the card.'
                          }
                        >
                          <CardNumberElement
                            options={cardNumberOptions}
                            onChange={(event) => {
                              setFieldValue(
                                'cardNumberValidation',
                                event.complete,
                              );
                            }}
                          />
                        </PayElementField>

                        {/* Expiry before security code, as they read on the card. */}
                        <div className="pay-fields__pair">
                          <PayElementField
                            id="card-expiry"
                            label="Expiry date"
                            error={
                              fieldError('expiryDateValidation') &&
                              'Enter the expiry date as MM / YY.'
                            }
                          >
                            <CardExpiryElement
                              options={cardExpiryOptions}
                              onChange={(event) => {
                                setFieldValue(
                                  'expiryDateValidation',
                                  event.complete,
                                );
                              }}
                            />
                          </PayElementField>

                          <PayElementField
                            id="card-cvc"
                            label="Security code"
                            hint="The last three digits on the back of the card."
                            error={
                              fieldError('cvcValidation') &&
                              'Enter the security code.'
                            }
                          >
                            <CardCvcElement
                              options={cardCvcOptions}
                              onChange={(event) => {
                                setFieldValue('cvcValidation', event.complete);
                              }}
                            />
                          </PayElementField>
                        </div>
                      </div>
                    ) : (
                      <div className="pay-fields">
                        {/*
                          The Klarna path validates the email into `name` —
                          the two branches are exclusive, and `outstandingItems`
                          relabels the key accordingly.
                        */}
                        <PayTextField
                          id="klarna-email"
                          label="Email address"
                          type="email"
                          value={klarnaEmail}
                          onChange={(e) => setKlarnaEmail(e.target.value)}
                          autoComplete="email"
                          placeholder="you@example.com"
                          error={
                            fieldError('name') &&
                            'Enter the email address Klarna should use.'
                          }
                        />

                        <PayTextField
                          id="name"
                          name="name"
                          label="Full name"
                          value={values.name}
                          onChange={handleChange}
                          autoComplete="name"
                          placeholder="Your full name"
                        />

                        <Callout tone="info">
                          We will send you to Klarna to finish paying, then
                          bring you straight back.
                        </Callout>
                      </div>
                    )}
                  </BookingLeg>

                  <ApplePayButton
                    paymentState={state}
                    appliedDiscount={appliedDiscount}
                    onPaymentSuccess={handleApplePaySuccess}
                    isEnabled={values.agreeToTerms && values.agreeToPackingRules && !submitting}
                  />

                  <BookingLeg
                    step={2}
                    id="pay-billing"
                    title="Billing address"
                    lead="The address this card is registered to."
                  >
                    <SelectableCard
                      variant="multiple"
                      label="Same as the collection address"
                      description="International cards are more likely to be accepted with the collection address."
                      checked={selectedBillingOption === 'collection'}
                      onChange={(next) => {
                        if (!next) {
                          setSelectedBillingOption(null);
                          setShowBillingAddressLookup(true);
                        } else {
                          setSelectedBillingOption('collection');
                          setBillingAddress(collectionAddress);
                          setShowBillingAddressLookup(false);
                          if (!billingEventSentToGTM) {
                            setBillingEventSentToGTM(true);
                            eventTrackingCallback(eecBillingObject);
                            eventTrackingCallback(billingInfoObject);
                          }
                        }
                      }}
                    />

                    {showBillingAddressLookup && (
                      <AddressLookup
                        address={billingAddress}
                        setAddress={setBillingAddress}
                        callback={eventTrackingCallback}
                        eecBillingInfo={eecBillingObject}
                        billingInfo={billingInfoObject}
                        billingEventSentToGTM={billingEventSentToGTM}
                        setBillingEventSentToGTM={setBillingEventSentToGTM}
                      />
                    )}

                    {fieldError('address') && (
                      <p className="pay-field__error">
                        Enter the billing address for this card, including its
                        postcode.
                      </p>
                    )}
                  </BookingLeg>
                </div>

                <aside
                  className="pay-page__aside"
                  aria-labelledby="pay-order-title"
                >
                  <h2 id="pay-order-title" className="pay-order__heading">
                    Your order
                  </h2>

                  <div className="pay-order">
                  {/* Pay Now Section */}
                  <OrderGroup
                    id="pay-order-now"
                    name="Pay Now"
                    primary
                    open={showPayNowDetails}
                    onToggle={() => setShowPayNowDetails(!showPayNowDetails)}
                    figure={
                      payNowTotal()
                    }
                  >
                    {state.removalService && state.selectedTier ? (
                      <>
                        <OrderLine
                          term={`${state.selectedTier.name} Removal`}
                          value={toMoney(state.selectedTier.price)}
                        />
                        {state.selectedPacking && (
                          <OrderLine
                            term={state.selectedPacking.name}
                            value={toMoney(state.selectedPacking.price)}
                          />
                        )}
                        {state.questions?.hasDisassembly && (
                          <OrderLine
                            term="Furniture Disassembly Service"
                            value={toMoney(FURNITURE_DISASSEMBLY_FEE)}
                          />
                        )}
                        {appliedDiscount?.upFront && (
                          <OrderLine
                            variant="discount"
                            term={`Discount (${appliedDiscount.discountPercentage}%)`}
                            value={`-${toMoney(
                              (removalsPayNowBase() *
                                appliedDiscount.discountPercentage) /
                                100,
                            )}`}
                          />
                        )}
                      </>
                    ) : (
                      boxes.map(
                        (box) =>
                          box.count > 0 && (
                            <OrderLine
                              key={box.id}
                              term={
                                <>
                                  {box.name} &times; {box.count}
                                </>
                              }
                              value={boxToMoney(box)}
                            />
                          ),
                      )
                    )}

                    {needsEmptyBoxDelivery && (
                      <OrderLine
                        term="Delivery of empty boxes"
                        value={toMoney(deliveryFee)}
                      />
                    )}

                    {storageRequired && (
                      <>
                        <OrderLine
                          term="Collection fee"
                          value={toMoney(COLLECTION_FEE)}
                        />

                        {collectionAddress.postCode &&
                          isOutOfArea(collectionAddress.postCode) && (
                            <OrderLine
                              term="Collection surcharge"
                              value={toMoney(COLLECTION_SURCHARGE)}
                            />
                          )}
                      </>
                    )}

                    {!storageRequired && !suitcases && (
                      <OrderLine
                        term={
                          <>
                            Shipping Cost (Box &times; {totalBoxesCount})
                          </>
                        }
                        value={toMoney(deliveryCost)}
                      />
                    )}

                    {appliedDiscount && appliedDiscount.upFront && (
                      <OrderLine
                        variant="discount"
                        term="Discount applied"
                        value={`-${appliedDiscount.discountPercentage}% (${calculateDiscountAmount()})`}
                      />
                    )}
                  </OrderGroup>

                  {showMonthlyGroup && (
                    <OrderGroup
                      id="pay-order-monthly"
                      name="Pay Monthly"
                      asterisk="*"
                      open={showPayMonthlyDetails}
                      onToggle={() =>
                        setShowPayMonthlyDetails(!showPayMonthlyDetails)
                      }
                      figure={
                        state.removalService && state.selectedStorage
                          ? toMoney(removalsMonthlyTotal())
                          : monthlyCostToMoney(boxes, appliedDiscount)
                      }
                    >
                          {state.removalService && state.selectedStorage ? (
                            <>
                              <OrderLine
                                term={`${state.selectedStorage.name} (${state.selectedStorage.size})`}
                                value={toMoney(state.selectedStorage.price)}
                              />
                              {appliedDiscount && appliedDiscount.recurring && (
                                <OrderLine
                                  variant="discount"
                                  term={`Discount (${appliedDiscount.discountPercentage}%)`}
                                  value={`-${toMoney(
                                    (state.selectedStorage.price *
                                      appliedDiscount.discountPercentage) /
                                      100,
                                  )}`}
                                />
                              )}
                            </>
                          ) : (
                            <>
                              {boxes.map(
                                (box) =>
                                  box.count > 0 && (
                                    <OrderLine
                                      key={box.id}
                                      term={
                                        <>
                                          {box.name} &times; {box.count}
                                        </>
                                      }
                                      value={toMoney(
                                        box.storageCost * box.count,
                                      )}
                                    />
                                  ),
                              )}

                              {appliedDiscount && appliedDiscount.recurring && (
                                <OrderLine
                                  variant="discount"
                                  term="Discount applied"
                                  value={`-${appliedDiscount.discountPercentage}% (${calculateMonthlyDiscountAmount()})`}
                                />
                              )}
                            </>
                          )}
                    </OrderGroup>
                  )}

                  {showRedeliveryGroup && (
                    <OrderGroup
                      id="pay-order-final"
                      name="Redelivery"
                      asterisk="**"
                      open={showFinalPaymentDetails}
                      onToggle={() =>
                        setShowFinalPaymentDetails(!showFinalPaymentDetails)
                      }
                      figure={toMoney(finalPaymentTotal)}
                    >
                          {numberOfBoxesWithReturnFee > 0 && (
                            <OrderLine
                              term={
                                <>
                                  Redelivery of boxes &times;{' '}
                                  {numberOfBoxesWithReturnFee}
                                </>
                              }
                              value={toMoney(returnFeeTotal)}
                            />
                          )}

                          {differentAreaSurcharge > 0 && (
                            <OrderLine
                              term={
                                <>
                                  Different area surcharge (
                                  {numberOfBoxesWithReturnFee} boxes)
                                </>
                              }
                              value={toMoney(differentAreaSurcharge)}
                            />
                          )}
                    </OrderGroup>
                  )}

                  {(showMonthlyGroup || showRedeliveryGroup) && (
                    <div className="pay-order__notes">
                      {showMonthlyGroup && (
                        <p>
                          *Subject to change if we collect a different number
                          of boxes than booked.
                        </p>
                      )}
                      {showRedeliveryGroup && (
                        <p>
                          **Redelivery due 5 days before your boxes are
                          returned to you.
                        </p>
                      )}
                    </div>
                  )}
                  </div>

                  <DiscountComponent
                    successfulDiscount={(discount) =>
                      setAppliedDiscount(discount)
                    }
                  />

              <div className="pay-confirm">
                <div className="pay-confirm__terms">
                  <Checkbox
                    id="agree-to-terms"
                    name="agreeToTerms"
                    checked={values.agreeToTerms}
                    onChange={() => {
                      setFieldValue('agreeToTerms', !values.agreeToTerms);
                    }}
                  />

                  <label htmlFor="agree-to-terms">
                    I agree to the{' '}
                    <a
                      href="https://www.kitkeeper.co.uk/terms-and-conditions/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      terms and conditions
                    </a>
                  </label>
                </div>

                <div className="pay-confirm__terms">
                  <Checkbox
                    id="agree-to-packing-rules"
                    name="agreeToPackingRules"
                    checked={values.agreeToPackingRules}
                    onChange={() => {
                      setFieldValue(
                        'agreeToPackingRules',
                        !values.agreeToPackingRules,
                      );
                    }}
                  />

                  <label htmlFor="agree-to-packing-rules">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowPackingModal(true)}
                    >
                      packing requirements
                    </button>
                  </label>
                </div>

                {/*
                  What is left to do, named. The CTA is described by it, so
                  the reason a payment cannot be taken yet reaches assistive
                  tech at the control the customer is trying to use.
                */}
                <p
                  className={`pay-confirm__status${
                    attempted && outstanding.length > 0
                      ? ' pay-confirm__status--alert'
                      : ''
                  }`}
                  id="pay-confirm-status"
                >
                  {outstanding.length === 0
                    ? paymentMethodType === 'klarna'
                      ? 'Ready — we will take you to Klarna to finish.'
                      : 'Ready to pay.'
                    : attempted
                    ? `Still needed: ${listSentence(
                        outstanding.map((entry) => entry.label),
                      )}.`
                    : 'Check your details, then confirm below.'}
                </p>

                {/*
                  These were `<h3>` pairs — one heading reading "Error: " and
                  a second carrying the message — so nothing announced them
                  and each failure added two entries to the page outline.
                */}
                {submitting && (
                  <p
                    className="pay-confirm__message pay-confirm__message--busy"
                    role="status"
                    aria-live="polite"
                  >
                    Taking payment. Please do not refresh the page.
                  </p>
                )}

                {error && (
                  <p
                    className="pay-confirm__message pay-confirm__message--error"
                    role="alert"
                  >
                    {error}
                  </p>
                )}

                {paymentError && (
                  <p
                    className="pay-confirm__message pay-confirm__message--error"
                    role="alert"
                  >
                    {paymentError}
                  </p>
                )}

                {/*
                  Deliberately not `disabled={!isValid}`. A disabled submit
                  cannot be attempted, so it cannot reveal which of seven
                  requirements is missing — the customer is left pressing a
                  dead control. Same call as Track 4.8.
                */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  className="pay-confirm__action"
                  loading={submitting}
                  disabled={submitting}
                  rightIcon={<SummaryArrow />}
                  aria-describedby="pay-confirm-status"
                >
                  {paymentMethodType === 'klarna'
                    ? 'Continue to Klarna'
                    : 'Confirm payment'}
                </Button>
              </div>
                </aside>
              </div>
            </form>
            );
          }}
        </Formik>
      </div>
      {pathname.includes('3ds-secure') && (
        <ThreeDSModal url={threeDSRedirectUrl} />
      )}

      {/* Packing Requirements Modal */}
      <PackingRequirementsModal
        isOpen={showPackingModal}
        onClose={() => setShowPackingModal(false)}
      />
    </>
  );
}


function PaymentWrapper(props: PaymentProps) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentComponent {...props} />
    </Elements>
  );
}

export default withRouter(PaymentWrapper);
